import { Check, Building, Droplets, Zap } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const DISCIPLINE_META = {
  civil: { icon: Building, image: IMAGES.construcaoSeca },
  ambiental: { icon: Droplets, image: IMAGES.eteEta },
  eletrica: { icon: Zap, image: IMAGES.fotovoltaica },
};

const Disciplines = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="disciplines-section" className="bg-[#07120C] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <Reveal>
          <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-emerald-400">
            {t.disciplines.eyebrow}
          </p>
          <h2 className="font-display mt-4 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t.disciplines.title}
          </h2>
        </Reveal>

        <div className="mt-20 space-y-24 lg:space-y-32">
          {t.disciplines.items.map((d, i) => {
            const meta = DISCIPLINE_META[d.id];
            return (
              <div
                key={d.id}
                data-testid={`discipline-${d.id}`}
                className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
              >
                <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="group relative overflow-hidden rounded-3xl border border-emerald-500/20">
                    <img
                      src={meta.image}
                      alt={d.alt}
                      loading="lazy"
                      className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:h-[26rem]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07120C]/80 via-transparent to-transparent" />
                    <span className="font-mono-tech absolute left-5 top-5 rounded-full border border-emerald-500/30 bg-[#07120C]/70 px-4 py-1.5 text-xs tracking-[0.3em] text-emerald-400 backdrop-blur-md">
                      {d.code} / {d.eyebrow.toUpperCase()}
                    </span>
                  </div>
                </Reveal>

                <Reveal delay={0.15} className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10">
                      <meta.icon className="h-5 w-5 text-emerald-400" />
                    </span>
                    <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-emerald-400">
                      {d.eyebrow}
                    </p>
                  </div>
                  <h3 className="font-display mt-5 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl">
                    {d.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-slate-400">{d.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {d.bullets.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-slate-300 sm:text-base">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-emerald-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-2.5">
                    {d.chips.map((chip) => (
                      <span
                        key={chip}
                        className="font-mono-tech rounded-full border border-emerald-500/25 bg-emerald-500/5 px-4 py-1.5 text-xs uppercase tracking-[0.15em] text-emerald-300"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Disciplines;
